import React from 'react';
import {
    PieChart,
    Pie,
    Cell,
    ResponsiveContainer,
    Tooltip
} from 'recharts';
import { Box, Typography, Grid, Paper } from '@mui/material';

const COLORS = {
    agree: '#00e396',      // Žalia
    disagree: '#ff4560',   // Raudona
    unknown: '#008ffb'     // Mėlyna
};

const CustomLabel = ({ cx, cy, midAngle, innerRadius, outerRadius, value }) => {
    if (value <= 1) return null;
    
    const RADIAN = Math.PI / 180;
    const radius = outerRadius + 20; // Padidiname radius, kad tekstas būtų toliau nuo skritulio
    const x = cx + radius * Math.cos(-midAngle * RADIAN);
    const y = cy + radius * Math.sin(-midAngle * RADIAN);
    
    // Nustatome text-anchor pagal poziciją
    const textAnchor = x >= cx ? 'start' : 'end';

    return (
        <text
            x={x}
            y={y}
            fill="#333"
            textAnchor={textAnchor}
            dominantBaseline="central"
            style={{
                fontSize: '14px',
                fontWeight: 500
            }}
        >
            {`${value.toFixed(1)}%`}
        </text>
    );
};

const SinglePieChart = ({ item, legend = [], index }) => {
    const pieData = item.ats
        .map((ans, ansIndex) => ({
            name: legend[ansIndex].name,
            value: ans.val || 0,
            color: Object.values(COLORS)[ansIndex]
        }))
        .filter(d => d.value > 0);

    return (
        <Grid item xs={12} sm={6} lg={4} key={index} sx={{ mb: 4, display: 'flex', justifyContent: 'center' }}>
            <Paper 
                elevation={1}
                sx={{ 
                    p: 1,
                    height: '100%',
                    width: '100%',
                    maxWidth: '350px',
                    backgroundColor: '#ffffff',
                    boxShadow: '0 1px 3px rgba(0,0,0,0.12)',
                    '&:hover': {
                        boxShadow: '0 2px 5px rgba(0,0,0,0.1)'
                    },
                    position: 'relative'
                }}
            >
                <Typography
                    variant="h6"
                    sx={{
                        fontWeight: 500,
                        fontSize: '0.95rem',
                        color: '#333',
                        lineHeight: 1.4,
                        mt: 2,
                        minHeight: '2.8rem',
                        textAlign: 'center'
                    }}
                >
                    {item.text}
                </Typography>
                <Box sx={{ 
                    height: 300, 
                    display: 'flex', 
                    flexDirection: 'column',
                    alignItems: 'center', 
                    justifyContent: 'space-between',
                    position: 'relative',
                    width: '100%',
                    mx: 'auto'
                }}>
                    <Box sx={{ 
                        height: 260,
                        width: '100%',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center'
                    }}>
                    <ResponsiveContainer>
                        <PieChart>
                            <Pie
                                data={pieData}
                                cx="50%"
                                cy="50%"
                                innerRadius={45}
                                outerRadius={75}
                                paddingAngle={3}
                                dataKey="value"
                                label={CustomLabel}
                                labelLine={false}
                                strokeWidth={1}
                                stroke="#fff"
                            >
                                {pieData.map((entry, index) => (
                                    <Cell 
                                        key={`cell-${index}`}
                                        fill={entry.color}
                                    />
                                ))}
                            </Pie>
                            <Legend
                                verticalAlign="middle"
                                align="right"
                                layout="vertical"
                                iconType="circle"
                                iconSize={8}
                                formatter={(value, entry) => {
                                    const item = pieData.find(d => d.name === value);
                                    return `${value} (${item?.value.toFixed(1)}%)`;
                                }}
                                wrapperStyle={{
                                    paddingLeft: '15px',
                                    fontSize: '11px',
                                    lineHeight: '1.2',
                                    color: '#333'
                                }}
                            />
                            <Tooltip 
                                formatter={(value) => `${value.toFixed(1)}%`}
                                contentStyle={{
                                    backgroundColor: '#fff',
                                    border: '1px solid #ddd',
                                    borderRadius: '4px',
                                    padding: '8px',
                                    boxShadow: '0 2px 4px rgba(0,0,0,0.1)'
                                }}
                            />
                        </PieChart>
                    </ResponsiveContainer>
                    </Box>
                    <Box sx={{ 
                        display: 'flex', 
                        justifyContent: 'center', 
                        gap: 2,
                        mt: 1
                    }}>
                        {legend.map((item, i) => (
                            <Box 
                                key={i} 
                                sx={{ 
                                    display: 'flex', 
                                    alignItems: 'center',
                                    gap: 0.5
                                }}
                            >
                                <Box 
                                    sx={{ 
                                        width: 12, 
                                        height: 12, 
                                        borderRadius: '50%',
                                        backgroundColor: Object.values(COLORS)[i]
                                    }} 
                                />
                                <Typography 
                                    variant="caption" 
                                    sx={{ 
                                        fontSize: '0.75rem',
                                        color: '#666'
                                    }}
                                >
                                    {item.name}
                                </Typography>
                            </Box>
                        ))}
                    </Box>
                </Box>
            </Paper>
        </Grid>
    );
};

const Legend = ({ legend }) => legend ? (
    <Box 
        sx={{ 
            px: 3,
            backgroundColor: 'transparent'
        }}
    >
        <Grid container spacing={3} justifyContent="center">
            {legend.map((item, index) => (
                <Grid item key={item.name}>
                    <Box sx={{ 
                        display: 'flex', 
                        alignItems: 'center', 
                        gap: 1.5 
                    }}>
                        <Box
                            sx={{
                                width: 20,
                                height: 20,
                                backgroundColor: Object.values(COLORS)[index],
                                borderRadius: 1,
                                transition: 'transform 0.2s ease-in-out',
                                '&:hover': {
                                    transform: 'scale(1.1)'
                                }
                            }}
                        />
                        <Typography 
                            variant="subtitle1"
                            sx={{ 
                                fontWeight: 500,
                                color: '#333'
                            }}
                        >
                            {item.name}
                        </Typography>
                    </Box>
                </Grid>
            ))}
        </Grid>
    </Box>
) : null;

export const PieChartComponent = ({ data, legend }) => {
    return (
        <Box sx={{ 
            width: '100%', 
            position: 'relative', 
            p: 0,
            overflow: 'hidden'
        }}>
            <Grid 
                container 
                spacing={2}
                justifyContent="center"
                sx={{
                    width: '100%',
                    m: 0,
                    '& .MuiGrid-item': {
                        display: 'flex',
                        justifyContent: 'center',
                        p: 1
                    }
                }}
            >
                {data.map((item, index) => (
                    <SinglePieChart 
                        key={index}
                        item={item}
                        legend={legend}
                        index={index}
                    />
                ))}
            </Grid>
        </Box>
    );
};